import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGet, apiSend } from '../api/client';
import { fetchGameById } from '../api/catalog';
import { fetchWalletSnapshot } from '../api/payments';
import { closeIdlePlaySessions } from '../api/preferences';
import { touchRecent } from '../api/playerGames';
import type { Game } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import {
  type AggLaunchStatus,
  type FundingChoice,
  catalogReturnPath,
  launchErrorMessage,
  looksLikeVerifiedSlot,
  pickDefaultFunding,
  restoreCatalogPosition,
} from '../lib/gameLaunch';

export function PlayPage() {
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();
  const forceBonus = searchParams.get('funding') === 'bonus';
  const navigate = useNavigate();
  const { t } = useTranslation(['games', 'common']);
  const { user } = useAuth();

  const [game, setGame] = useState<Game | null>(null);
  const [phase, setPhase] = useState<'loading' | 'bonus_prompt' | 'playing' | 'error'>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const [showExternalFallback, setShowExternalFallback] = useState(false);
  const startedFor = useRef<string | null>(null);
  const iframeLoaded = useRef(false);
  const activeSessionId = useRef<string | null>(null);
  const t0 = useRef(typeof performance !== 'undefined' ? performance.now() : 0);

  const slotEligible = useMemo(() => (game ? looksLikeVerifiedSlot(game) : false), [game]);

  const runDemo = useCallback(async () => {
    if (!gameId) return;
    setPhase('loading');
    setMessage(null);
    try {
      const res = await apiSend<{ url?: string }>('POST', '/api/v1/aggregator/demo-sessions', {
        game_id: gameId,
      });
      if (!res.url) {
        setPhase('error');
        setMessage(t('games:demoUnavailable'));
        return;
      }
      if (user) void touchRecent(gameId).catch(() => undefined);
      setLaunchUrl(res.url);
      setPhase('playing');
      setShowExternalFallback(false);
      if (typeof performance !== 'undefined') {
        console.info('[vbb-launch] demo_url_ms', Math.round(performance.now() - t0.current));
      }
    } catch (err) {
      setPhase('error');
      setMessage(launchErrorMessage(err, t('games:demoUnavailable'), t));
    }
  }, [gameId, t, user]);

  const runWallet = useCallback(
    async (funding: FundingChoice) => {
      if (!gameId) return;
      setPhase('loading');
      setMessage(null);
      try {
        const res = await apiSend<{ url?: string; session_id?: string; play_currency?: string }>(
          'POST',
          '/api/v1/aggregator/sessions',
          {
            game_id: gameId,
            funding_source: funding,
          },
        );
        if (!res.url) {
          setPhase('error');
          setMessage(t('games:realMoneyDisabled'));
          return;
        }
        activeSessionId.current = res.session_id || null;
        if (user) void touchRecent(gameId).catch(() => undefined);
        setLaunchUrl(res.url);
        setPhase('playing');
        setShowExternalFallback(false);
        if (typeof performance !== 'undefined') {
          console.info('[vbb-launch] real_url_ms', Math.round(performance.now() - t0.current));
        }
      } catch (err) {
        setPhase('error');
        setMessage(launchErrorMessage(err, t('games:realMoneyDisabled'), t));
      }
    },
    [gameId, t, user],
  );

  // Resolve game + aggregator + wallet in parallel, then launch once — no catalog preload.
  useEffect(() => {
    if (!gameId) return;
    const key = `${gameId}:${user?.public_id || 'anon'}:${forceBonus ? 'bonus' : 'auto'}`;
    if (startedFor.current === key) return;
    startedFor.current = key;
    t0.current = typeof performance !== 'undefined' ? performance.now() : 0;
    iframeLoaded.current = false;

    let cancelled = false;
    void (async () => {
      setPhase('loading');
      setMessage(null);
      setLaunchUrl(null);
      setShowExternalFallback(false);

      const [aggResult, gameResult, walletResult] = await Promise.all([
        apiGet<AggLaunchStatus>('/api/v1/aggregator/status').catch(() => null),
        fetchGameById(gameId).catch(() => null),
        user ? fetchWalletSnapshot().catch(() => null) : Promise.resolve(null),
      ]);
      if (cancelled) return;

      if (typeof performance !== 'undefined') {
        console.info('[vbb-launch] preload_ms', Math.round(performance.now() - t0.current));
      }

      const resolved = gameResult;
      setGame(resolved);
      if (!resolved) {
        setPhase('error');
        setMessage(t('games:notFound', { defaultValue: 'Game not found' }));
        return;
      }

      // Record history for every provider as soon as the player opens a real catalog game.
      if (user) void touchRecent(gameId).catch(() => undefined);

      if (forceBonus) {
        if (!user) {
          setPhase('error');
          setMessage(t('games:loginToPlay'));
          return;
        }
        if (!aggResult?.bonus_launch_allowed) {
          setPhase('error');
          setMessage(t('games:bonusPlayDisabled'));
          return;
        }
        await runWallet('bonus');
        return;
      }

      const decision = pickDefaultFunding({
        agg: aggResult,
        game: resolved,
        loggedIn: Boolean(user),
        realMinor: Number(walletResult?.balance_real_minor ?? 0),
        bonusMinor: Number(walletResult?.balance_bonus_minor ?? 0),
      });

      if (decision.mode === 'login') {
        setPhase('error');
        setMessage(t('games:loginToPlay'));
        return;
      }
      if (decision.mode === 'blocked') {
        setPhase('error');
        setMessage(
          decision.reason === 'real_money_disabled'
            ? t('games:realMoneyDisabled')
            : t('games:demoUnavailable'),
        );
        return;
      }
      if (decision.mode === 'bonus_prompt') {
        setPhase('bonus_prompt');
        return;
      }
      if (decision.mode === 'demo') {
        await runDemo();
        return;
      }
      await runWallet('real');
    })();

    return () => {
      cancelled = true;
    };
  }, [forceBonus, gameId, runDemo, runWallet, t, user]);

  // Mark the launch session idle-closed when leaving the play page (safe: skips unsettled bets).
  useEffect(() => {
    return () => {
      const sid = activeSessionId.current;
      if (!sid || !user) return;
      void closeIdlePlaySessions(sid).catch(() => undefined);
      activeSessionId.current = null;
    };
  }, [user]);

  // Only offer external open if iframe never loads (provider/WebView block).
  useEffect(() => {
    if (phase !== 'playing' || !launchUrl) return;
    const timer = window.setTimeout(() => {
      if (!iframeLoaded.current) setShowExternalFallback(true);
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [phase, launchUrl]);

  function goBack() {
    const sid = activeSessionId.current;
    if (sid && user) void closeIdlePlaySessions(sid).catch(() => undefined);
    navigate(catalogReturnPath());
    restoreCatalogPosition();
  }

  function openExternal() {
    if (!launchUrl) return;
    window.open(launchUrl, '_blank', 'noopener,noreferrer');
  }

  if (!gameId) {
    return (
      <div className="page play-page">
        <p>{t('games:notFound', { defaultValue: 'Game not found' })}</p>
        <Link to="/" className="btn btn-primary">
          {t('games:backToCatalog', { defaultValue: 'Back to catalog' })}
        </Link>
      </div>
    );
  }

  return (
    <div className={`play-page${phase === 'playing' ? ' play-page-immersive' : ''}`}>
      {/* No in-app title bar: Telegram native BackButton handles return (see useTelegramChrome). */}

      {phase === 'loading' ? (
        <div className="play-loading" role="status">
          <div className="play-loading-spinner" aria-hidden="true" />
          <p>{t('common:loading')}</p>
        </div>
      ) : null}

      {phase === 'bonus_prompt' ? (
        <div className="play-prompt state-card">
          <h2>{t('games:bonusContinueTitle', { defaultValue: 'Bonus ilə davam?' })}</h2>
          <p className="muted-note">
            {t('games:bonusContinueBody', {
              defaultValue:
                'Əsas balans 0-dır. Bonus balansınız var — yalnız sizin seçiminizlə istifadə olunur.',
            })}
          </p>
          {!slotEligible ? <p className="muted-note">{t('games:bonusNotSlot')}</p> : null}
          <div className="game-launch-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!slotEligible}
              onClick={() => void runWallet('bonus')}
            >
              {t('games:launchBonus')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={goBack}>
              {t('common:back', { defaultValue: 'Geri' })}
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'error' ? (
        <div className="play-prompt state-card">
          <p>{message || t('games:demoUnavailable')}</p>
          <div className="game-launch-actions">
            <button type="button" className="btn btn-primary" onClick={goBack}>
              {t('games:backToCatalog', { defaultValue: 'Back to catalog' })}
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'playing' && launchUrl ? (
        <div className="play-frame-wrap">
          <iframe
            className="play-frame"
            title={game?.name || 'game'}
            src={launchUrl}
            allow="fullscreen; autoplay; payment; clipboard-read; clipboard-write"
            referrerPolicy="no-referrer-when-downgrade"
            onLoad={() => {
              iframeLoaded.current = true;
              setShowExternalFallback(false);
              if (typeof performance !== 'undefined') {
                console.info('[vbb-launch] iframe_load_ms', Math.round(performance.now() - t0.current));
              }
            }}
          />
          {showExternalFallback ? (
            <div className="play-frame-fallback">
              <p className="muted-note">
                {t('games:openExternalHint', {
                  defaultValue:
                    'Provayder oyunu daxili pəncərədə açmağa imkan vermir. Aşağıdakı düymə ilə davam edin.',
                })}
              </p>
              <button type="button" className="btn btn-primary" onClick={openExternal}>
                {t('games:openExternal', { defaultValue: 'Oyunu aç' })}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
